import React, { useState } from 'react';
import { Calendar, Download, Info, Filter } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для отображения графика работы
 */
const WorkScheduleTable = ({ 
  workSchedule, 
  results, 
  showFullSchedule, 
  setShowFullSchedule 
}) => {
  const [activeFilter, setActiveFilter] = useState('all');
  
  if (!workSchedule || workSchedule.length === 0 || !results) {
    return null;
  }

  // Для отображения только первого месяца, если не показан полный график
  const displaySchedule = showFullSchedule 
    ? workSchedule 
    : workSchedule.slice(0, Math.min(31, workSchedule.length));

  // Фильтрация отображаемого графика
  const filteredSchedule = displaySchedule.filter(item => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'workdays') return item.dayStatus === 'workday' || item.dayStatus === 'shortened' || item.dayStatus === 'holiday-workday';
    if (activeFilter === 'weekends') return item.dayStatus === 'weekend';
    if (activeFilter === 'holidays') return item.dayStatus === 'holiday' || item.holidayInfo;
    return true;
  });

  // Названия дней недели
  const weekdayNames = [
    'Воскресенье', 'Понедельник', 'Вторник', 'Среда', 
    'Четверг', 'Пятница', 'Суббота'
  ];

  // Короткие названия дней недели
  const weekdayShortNames = [
    'Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'
  ];

  // Статусы дней
  const statusNames = {
    'workday': 'Рабочий день',
    'weekend': 'Выходной',
    'holiday': 'Праздник',
    'shortened': 'Сокращенный день',
    'holiday-workday': 'Праздничный рабочий день'
  };

  // Цвета для статусов
  const statusColors = {
    'workday': 'bg-cyan-50 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-200',
    'weekend': 'bg-gray-50 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
    'holiday': 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200',
    'shortened': 'bg-purple-50 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200',
    'holiday-workday': 'bg-orange-50 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200'
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-cyan-500" />
          График рабочих дней ({workSchedule.length} дней)
        </h3>

        {/* Кнопка переключения полного/краткого графика */}
        {workSchedule.length > 31 && (
          <Button
            size="sm"
            onClick={() => setShowFullSchedule(prev => !prev)}
            variant="outline"
            aria-expanded={showFullSchedule}
          >
            {showFullSchedule ? 'Показать краткий график' : `Показать все ${workSchedule.length} дней`}
          </Button>
        )}
      </div>

      {/* Фильтры */}
      <div className="mb-4 flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={activeFilter === 'all' ? 'primary' : 'outline'}
          onClick={() => setActiveFilter('all')}
          icon={<Filter className="w-3 h-3" />}
        >
          Все дни
        </Button>
        <Button
          size="sm"
          variant={activeFilter === 'workdays' ? 'primary' : 'outline'}
          onClick={() => setActiveFilter('workdays')}
        >
          Рабочие дни
        </Button>
        <Button
          size="sm"
          variant={activeFilter === 'weekends' ? 'primary' : 'outline'}
          onClick={() => setActiveFilter('weekends')}
        >
          Выходные
        </Button>
        <Button
          size="sm"
          variant={activeFilter === 'holidays' ? 'primary' : 'outline'}
          onClick={() => setActiveFilter('holidays')}
        >
          Праздники
        </Button>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>{showFullSchedule 
            ? "Полный график всех дней в выбранном периоде. Прокрутите таблицу вниз для просмотра всех данных." 
            : `Показаны первые ${displaySchedule.length} дней периода. Нажмите "Показать все дни" для просмотра полного графика.`
          }</p>
        </div>
      </div>

      {/* Легенда статусов */}
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(statusNames).map(([status, name]) => (
          <div 
            key={status} 
            className={`px-2 py-1 rounded text-xs ${statusColors[status]}`}
          >
            {name}
          </div>
        ))}
      </div>

      {/* Контейнер таблицы с прокруткой */}
      <div className="overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[480px] work-schedule-table">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
            <tr>
              <th scope="col" className="px-3 py-2 font-semibold text-center">Дата</th>
              <th scope="col" className="px-3 py-2 font-semibold">День недели</th>
              <th scope="col" className="px-3 py-2 font-semibold">Статус</th>
              <th scope="col" className="px-3 py-2 font-semibold text-right">Раб. часы</th>
              {results.totalNightHours > 0 && (
                <th scope="col" className="px-3 py-2 font-semibold text-right">Ночные часы</th>
              )}
              <th scope="col" className="px-3 py-2 font-semibold">Примечание</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredSchedule.map((item, index) => (
              <tr
                key={index}
                className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100`}
              >
                <td className="px-3 py-1.5 text-center font-medium whitespace-nowrap">
                  {item.date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  {weekdayNames[item.dayOfWeek]}
                </td>
                <td className="px-3 py-1.5">
                  <span className={`px-2 py-1 rounded text-xs whitespace-nowrap ${statusColors[item.dayStatus]}`}>
                    {statusNames[item.dayStatus] || item.dayStatus}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  {item.workHours > 0 ? `${item.workHours} ч.` : '-'}
                </td>
                {results.totalNightHours > 0 && (
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    {item.nightHours > 0 ? `${item.nightHours} ч.` : '-'}
                  </td>
                )}
                <td className="px-3 py-1.5">
                  {item.holidayInfo || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Отсутствие данных при фильтрации */}
      {filteredSchedule.length === 0 && (
        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
          Нет данных для выбранного фильтра
        </div>
      )}

      {/* Кнопка показа полного графика (если он скрыт и больше предпросмотра) */}
      {!showFullSchedule && workSchedule.length > 31 && (
        <div className="text-center mt-4">
          <Button
            onClick={() => setShowFullSchedule(true)}
            icon={<Calendar className="w-4 h-4" />}
            variant="link"
          >
            Показать все {workSchedule.length} дней
          </Button>
        </div>
      )}
    </div>
  );
};

export default WorkScheduleTable;